import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";
import { DownloadIconButton, ViewIconButton } from "components/DatatablesActionIcons";
import ApprovedProofCard from "components/ApprovedProofCard";
import {
  approveClientProjectProof,
  ClientProjectDetail,
  ClientProjectFile,
  ClientProjectMessage,
  fetchClientProjectById,
  fetchClientProjectFiles,
  fetchClientProjectMessages,
  getDesignRequestBase,
  getLinkedOrderPath,
  getDesignRequestRoleBase,
  postClientProjectMessage,
  publicWorkDriveDownloadUrl,
  uploadClientProjectFiles,
} from "components/designRequestApi";
import {
  approvePublicProjectProof,
  createPublicAddressForProjectUser,
  extractUploadedFileIds,
  fetchProjectPublicAccess,
  fetchPublicUserAddresses,
  fetchUserAddressesByUserId,
  type PublicUserAddress,
} from "components/proejctsandquoteapicalls";
import AddAddressModal, {
  BillingShippingApprovalModal,
  type AddressFormValues,
} from "components/PublicAddressFormModel";
import "pages/signage/projectandquote-view.css";

function formatDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function statusDisplay(status: string | undefined): string {
  if (!status) return "—";
  return status
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function fileDisplayName(f: ClientProjectFile): string {
  return f.original_name || f.file_name || "File";
}

function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isVendorSender(senderType?: string): boolean {
  const s = String(senderType || "").toLowerCase();
  return s.includes("designer") || s.includes("signage") || s.includes("vendor") || s.includes("admin");
}

function formatConversationRole(senderType?: string, projectLicenseType?: string): string {
  const st = String(senderType || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (st === "ADMIN" || st === "DESIGNER" || st === "DESIGN_TEAM" || st === "SIGNAGE_FIRM") {
    return "Admin";
  }
  if (st === "BROKERAGE_TEAM_MEMBER" || st === "TEAM_MEMBER" || st === "TEAMMEMBER") {
    return "Teammber";
  }
  if (st === "BROKERAGE_AGENT") return "Brokerage Agent";
  if (st === "AGENT") {
    const lt = String(projectLicenseType || "")
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, "_");
    if (lt === "BROKERAGE_TEAM_MEMBER" || lt === "TEAM_MEMBER" || lt === "TEAMMEMBER") return "Teammber";
    if (lt === "BROKERAGE_AGENT") return "Brokerage Agent";
    return "Agent";
  }
  const lt = String(projectLicenseType || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (lt === "BROKERAGE_TEAM_MEMBER" || lt === "TEAM_MEMBER" || lt === "TEAMMEMBER") return "Teammber";
  if (lt === "BROKERAGE_AGENT") return "Brokerage Agent";
  if (lt === "AGENT") return "Agent";
  if (st) {
    return st
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  }
  return "User";
}

function fileTypeLabel(fileType: string | undefined, fileName: string): string {
  if (fileType?.includes("pdf")) return "PDF";
  if (fileType?.includes("png")) return "PNG";
  if (fileType?.includes("jpeg") || fileType?.includes("jpg")) return "JPG";
  const ext = fileName.split(".").pop()?.toUpperCase();
  return ext || "FILE";
}

function isHttpUrl(s: string | undefined): boolean {
  return /^https?:\/\//i.test(String(s || "").trim());
}

function isLikelyImagePreview(fileType: string | undefined, fileName: string): boolean {
  const t = (fileType || "").toLowerCase();
  if (t.includes("svg")) return false;
  if (t.includes("image/")) return true;
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  return ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);
}

function fileHref(f: ClientProjectFile): string {
  return f.file_url || publicWorkDriveDownloadUrl(f.file_id) || String(f.preview_url || "").trim();
}

const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

type ShippingMethodOption = {
  id: string;
  name: string;
  description: string;
  priceLabel: string;
};

const SHIPPING_METHODS: ShippingMethodOption[] = [
  { id: "Install", name: "Install", description: "", priceLabel: "TBD" },
  { id: "customer_pickup", name: "Customer Pickup", description: "Atlanta only · Free", priceLabel: "FREE" },
  { id: "farmer_delivery", name: "Farmer Delivery", description: "Delivered by our team", priceLabel: "TBD" },
  { id: "ups_shipping", name: "UPS Shipping", description: "Prices calculated once quote is created", priceLabel: "TBD" },
];

const pickAddressId = (addr: any): string => String(addr?._id || addr?.id || "").trim();

const formatAddressSingleLine = (addr: any): string => {
  const d = (addr?.address_details || {}) as any;
  const label = String(addr?.label || d?.label || "").trim();
  const line1 = String(d.address_line_1 || d.addressLine1 || "").trim();
  const line2 = String(d.address_line_2 || d.addressLine2 || "").trim();
  const city = String(d.city || "").trim();
  const state = String(d.state || "").trim();
  const zip = String(d.zip || "").trim();
  const addressCore = [line1, line2, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return label && addressCore ? `[${label}] ${addressCore}` : addressCore || label;
};

const toApprovalAddressPayload = (addr: PublicUserAddress | null) => {
  if (!addr) return null;
  const d = (addr.address_details || {}) as any;
  return {
    _id: String(addr._id || addr.id || "").trim(),
    label: String(addr.label || d.label || "").trim(),
    first_name: String(addr.first_name || "").trim(),
    last_name: String(addr.last_name || "").trim(),
    company: String(addr.company || "").trim(),
    default_address: Boolean(addr.default_address),
    address_details: {
      _id: String(d._id || "").trim(),
      label: String(d.label || addr.label || "").trim(),
      addressLine1: String(d.addressLine1 || d.address_line_1 || "").trim(),
      addressLine2: String(d.addressLine2 || d.address_line_2 || "").trim(),
      city: String(d.city || "").trim(),
      state: String(d.state || "").trim(),
      country: String(d.country || "").trim(),
      zip: String(d.zip || "").trim(),
      cityCode: String(d.cityCode || "").trim(),
      stateCode: String(d.stateCode || "").trim(),
      countryCode: String(d.countryCode || "").trim(),
      latitude: Number(d.latitude ?? 0),
      longitude: Number(d.longitude ?? 0),
      phone: String(d.phone || "").trim(),
    },
  };
};

function resolveCustomerGlobalUserId(project: ClientProjectDetail | null): string {
  return (
    String(project?.customer_global_user_id || "").trim() ||
    String(project?.agent_global_user_id || "").trim() ||
    String(project?.team_member_global_user_id || "").trim() ||
    String(getAuthCookie("globalUserId") || "").trim()
  );
}

function resolveCustomerMembershipId(project: ClientProjectDetail | null): string {
  return (
    String(project?.customer_membership_id || "").trim() ||
    String(project?.agent_membership_id || "").trim() ||
    String(project?.team_member_membership_id || "").trim() ||
    String(getAuthCookie("membershipId") || "").trim()
  );
}

function resolveCustomerOrganizationId(project: ClientProjectDetail | null): string {
  return (
    String(project?.vendor_org_id || "").trim() ||
    String(project?.brokerage_firm_organization_id || "").trim() ||
    String(getAuthCookie("organizationId") || "").trim()
  );
}

const DesignRequestView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const listPath = getDesignRequestBase(location.pathname);
  const roleBase = getDesignRequestRoleBase(location.pathname);
  const token = getAuthCookie("sessionToken") || "";

  const [project, setProject] = useState<ClientProjectDetail | null>(null);
  const [messages, setMessages] = useState<ClientProjectMessage[]>([]);
  const [sharedFiles, setSharedFiles] = useState<ClientProjectFile[]>([]);
  const [proofsByVersion, setProofsByVersion] = useState<Record<number, ClientProjectFile[]>>({});
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState<"conversation" | "files">("conversation");
  const [messageDraft, setMessageDraft] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [replyToMessageId, setReplyToMessageId] = useState("");
  const [selectedProofVersion, setSelectedProofVersion] = useState<number | null>(null);
  const [approving, setApproving] = useState(false);
  const [showAddressPickerModal, setShowAddressPickerModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [publicAddresses, setPublicAddresses] = useState<PublicUserAddress[]>([]);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState("");
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState("");
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState("ups_shipping");
  const [selectedFileQuantities, setSelectedFileQuantities] = useState<Record<string, number>>({});
  const [approvalPoNumber, setApprovalPoNumber] = useState("");
  const [showActions, setShowActions] = useState(false);
  const actionsMenuWrapRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const filePreviewOpenRef = useRef(false);
  const [filePreview, setFilePreview] = useState<{
    name: string;
    previewUrl: string;
    href: string;
    apiFileId?: string;
    sizeLabel: string;
    dateLabel: string;
  } | null>(null);

  const loadAll = useCallback(async () => {
    if (!token || !projectId) return;
    try {
      const [detail, msgs, files] = await Promise.all([
        fetchClientProjectById(token, projectId),
        fetchClientProjectMessages(token, projectId),
        fetchClientProjectFiles(token, projectId).catch(() => ({ sharedFiles: [], proofsByVersion: {} })),
      ]);
      setProject(detail);
      setMessages(msgs);
      setSharedFiles(files.sharedFiles);
      setProofsByVersion(files.proofsByVersion);
      setLoadError("");
      const versions = Object.keys(files.proofsByVersion).map(Number).filter((n) => Number.isFinite(n));
      setSelectedProofVersion((prev) => (prev == null && versions.length ? Math.max(...versions) : prev));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Failed to load design request");
    }
  }, [token, projectId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!actionsMenuWrapRef.current?.contains(e.target as Node)) setShowActions(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const resolvePublicAccessToken = useCallback(async (): Promise<string> => {
    if (!token || !projectId) return "";
    const publicAccessRes = await fetchProjectPublicAccess(token, projectId);
    const p = (publicAccessRes as any)?.data ?? (publicAccessRes as any) ?? {};
    const rawPublicUrl = String(p?.public_url || "").trim();
    const tokenFromPublicUrl = rawPublicUrl.includes("?")
      ? new URLSearchParams(rawPublicUrl.split("?")[1]).get("token") || ""
      : "";
    return String(p?.access_token || p?.accessToken || tokenFromPublicUrl || "").trim();
  }, [token, projectId]);

  const loadPublicAddresses = useCallback(async () => {
    const globalUserId = resolveCustomerGlobalUserId(project);
    const membershipId = resolveCustomerMembershipId(project);
    if (!globalUserId && !membershipId) {
      setPublicAddresses([]);
      return [] as PublicUserAddress[];
    }
    try {
      if (globalUserId) {
        const publicAccessToken = await resolvePublicAccessToken();
        const rows = await fetchPublicUserAddresses(
          globalUserId,
          resolveCustomerOrganizationId(project),
          publicAccessToken
        );
        const list = Array.isArray(rows) ? rows : [];
        if (list.length) {
          setPublicAddresses(list);
          return list;
        }
      }
    } catch {
      // try private route below
    }
    try {
      const privateId = membershipId || globalUserId;
      const privateRows = privateId ? await fetchUserAddressesByUserId(token, privateId) : [];
      const privateList = Array.isArray(privateRows) ? privateRows : [];
      if (privateList.length) {
        setPublicAddresses(privateList);
        return privateList;
      }
    } catch {
      // fallback below
    }
    setPublicAddresses([]);
    return [] as PublicUserAddress[];
  }, [project, token, resolvePublicAccessToken]);

  const proofVersionGroups = useMemo(
    () =>
      Object.keys(proofsByVersion)
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
        .map((version) => ({ version, files: proofsByVersion[version] || [] })),
    [proofsByVersion]
  );

  const approvedVersionNumbers = useMemo(() => {
    const set = new Set<number>();
    (project?.approved_proof_versions || []).forEach((v) => {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) set.add(n);
    });
    (project?.approved_proofs || []).forEach((row: any) => {
      const n = Number(row?.version);
      if (Number.isFinite(n) && n > 0) set.add(n);
    });
    proofVersionGroups.forEach(({ version, files }) => {
      if (files.some((f) => f.is_approved)) set.add(version);
    });
    return set;
  }, [project, proofVersionGroups]);

  const selectedProofFiles = selectedProofVersion ? proofsByVersion[selectedProofVersion] || [] : [];
  const selectedProofApproved =
    selectedProofVersion != null && approvedVersionNumbers.has(selectedProofVersion);
  const selectedProofFilesForApproval = useMemo(
    () =>
      selectedProofFiles.map((f) => ({
        id: f.id,
        name: fileDisplayName(f),
      })),
    [selectedProofFiles]
  );

  const otherSharedFiles = useMemo(
    () => sharedFiles.filter((f) => !f.is_proof),
    [sharedFiles]
  );

  const firstApprovedMessageIdByVersion = useMemo(() => {
    const map = new Map<number, string>();
    messages.forEach((m) => {
      const version = Number(m.proof_version || m.files.find((f) => f.is_proof)?.version || 0);
      if (!Number.isFinite(version) || version <= 0) return;
      if (!m.files.some((f) => f.is_proof)) return;
      if (!approvedVersionNumbers.has(version)) return;
      if (!map.has(version)) map.set(version, m.id);
    });
    return map;
  }, [messages, approvedVersionNumbers]);

  const approvedProofMetaByVersion = useMemo(() => {
    const map = new Map<
      number,
      {
        billingAddress?: any;
        shippingAddress?: any;
        poNumber?: string;
        quantity?: number;
        fileQuantities: Array<{ project_file_id: string; quantity: number }>;
        shippingMethod?: { name?: string; description?: string } | null;
      }
    >();
    (project?.approved_proofs || []).forEach((row: any) => {
      const version = Number(row?.version);
      if (!Number.isFinite(version) || version <= 0) return;
      map.set(version, {
        billingAddress: row?.billing_address ?? project?.billing_address,
        shippingAddress: row?.shipping_address ?? project?.shipping_address,
        poNumber: String(row?.po_number ?? row?.poNumber ?? (project as any)?.po_number ?? "").trim(),
        quantity: Number(row?.quantity ?? project?.quantity ?? 1),
        fileQuantities: Array.isArray(row?.file_quantities) ? row.file_quantities : [],
        shippingMethod: row?.shipping_method ?? project?.shipping_method ?? null,
      });
    });
    return map;
  }, [project]);

  const linkedOrderPath = useMemo(
    () =>
      getLinkedOrderPath(roleBase, {
        linked_order_id: project?.linked_order_id,
        linked_order_type: project?.linked_order_type,
      }),
    [project, roleBase]
  );

  const statusClass = useMemo(() => {
    const s = String(project?.status || "").toLowerCase();
    if (s === "design_approved") return "pq-status-pill design-approved";
    if (s === "quoted") return "pq-status-pill quoted";
    if (s === "completed") return "pq-status-pill completed";
    return "pq-status-pill in-progress";
  }, [project?.status]);

  const messagesById = useMemo(() => {
    const map = new Map<string, ClientProjectMessage>();
    messages.forEach((m) => {
      if (m.id) map.set(m.id, m);
    });
    return map;
  }, [messages]);

  const replyingToMessage = replyToMessageId ? messagesById.get(replyToMessageId) : undefined;

  const handleSend = async () => {
    if (!projectId || !token || sending) return;
    const text = messageDraft.trim();
    if (!text && attachedFiles.length === 0) return;
    setSending(true);
    try {
      let replyFileIds: string[] = [];
      if (attachedFiles.length) {
        const uploadRes = await uploadClientProjectFiles(token, projectId, attachedFiles);
        replyFileIds = extractUploadedFileIds(uploadRes);
      }
      const messageForApi = text || (replyFileIds.length ? "File Uploaded" : "");
      if (messageForApi || replyFileIds.length) {
        await postClientProjectMessage(token, projectId, messageForApi, {
          ...(replyToMessageId.trim() ? { reply_to_message_id: replyToMessageId.trim() } : {}),
          ...(replyFileIds.length ? { reply_to_file_ids: replyFileIds } : {}),
        });
      }
      setMessageDraft("");
      setAttachedFiles([]);
      setReplyToMessageId("");
      showSuccess("Message sent");
      await loadAll();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleApprove = async () => {
    if (!projectId || !token || !selectedProofVersion) return;
    try {
      await loadPublicAddresses();
    } catch {
      // address list stays empty; user can add one
    }
    setSelectedBillingAddressId("");
    setSelectedShippingAddressId("");
    setSameAsBilling(false);
    setSelectedShippingMethodId("ups_shipping");
    const nextQuantities: Record<string, number> = {};
    selectedProofFiles.forEach((f) => {
      if (f.id) nextQuantities[f.id] = 0;
    });
    setSelectedFileQuantities(nextQuantities);
    setApprovalPoNumber(
      String((project as any)?.po_number ?? (project as any)?.poNumber ?? "").trim()
    );
    if (filePreviewOpenRef.current) return;
    setFilePreview(null);
    setShowAddressPickerModal(true);
  };

  const performApproveSelectedProof = async () => {
    if (!projectId || !token || !selectedProofVersion) return;
    const billing = publicAddresses.find((a) => pickAddressId(a) === selectedBillingAddressId) || null;
    const shipping = sameAsBilling
      ? billing
      : publicAddresses.find((a) => pickAddressId(a) === selectedShippingAddressId) || null;
    const shippingMethod =
      SHIPPING_METHODS.find((m) => m.id === selectedShippingMethodId) || SHIPPING_METHODS[2];
    setApproving(true);
    try {
      const extraPayload = {
        versions: [selectedProofVersion],
        file_quantities: selectedProofFilesForApproval
          .map((file) => ({
            project_file_id: file.id,
            quantity:
              Number.isFinite(Number(selectedFileQuantities[file.id])) &&
              Number(selectedFileQuantities[file.id]) >= 0
                ? Math.floor(Number(selectedFileQuantities[file.id]))
                : 0,
          }))
          .filter((row) => row.project_file_id),
        billing_address: toApprovalAddressPayload(billing),
        shipping_address: toApprovalAddressPayload(shipping),
        both_address_same: Boolean(sameAsBilling),
        po_number: approvalPoNumber.trim(),
        shipping_method: {
          name: shippingMethod.name,
          description: shippingMethod.description,
        },
        updated_by: {
          global_user_id: String(getAuthCookie("globalUserId") || "").trim(),
          membership_id: String(getAuthCookie("membershipId") || "").trim(),
          organization_id: String(getAuthCookie("organizationId") || "").trim(),
          name: String(getAuthCookie("userName") || "").trim() || "Realtor",
        },
      };
      let publicAccessToken = "";
      try {
        publicAccessToken = await resolvePublicAccessToken();
      } catch {
        publicAccessToken = "";
      }
      if (publicAccessToken) {
        await approvePublicProjectProof(
          token,
          projectId,
          publicAccessToken,
          selectedProofVersion,
          undefined,
          extraPayload
        );
      } else {
        await approveClientProjectProof(token, projectId, {
          version: selectedProofVersion,
          ...extraPayload,
        });
      }
      showSuccess(`Proof V${selectedProofVersion} approved`);
      await loadAll();
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to approve proof");
    } finally {
      setApproving(false);
    }
  };

  const handleConfirmAddressAndApprove = async () => {
    const billing = publicAddresses.find((a) => pickAddressId(a) === selectedBillingAddressId) || null;
    const shipping = sameAsBilling
      ? billing
      : publicAddresses.find((a) => pickAddressId(a) === selectedShippingAddressId) || null;
    if (!billing || !shipping) {
      showError("Please select both billing and shipping addresses.");
      return;
    }
    setShowAddressPickerModal(false);
    await performApproveSelectedProof();
  };

  const handleSubmitApprovalAddress = async (values: AddressFormValues) => {
    if (!projectId || !token || !project) {
      showError("Project context is missing.");
      return;
    }
    const customerGlobalUserId = resolveCustomerGlobalUserId(project);
    if (!customerGlobalUserId) {
      showError("Customer identity is missing for this project.");
      return;
    }
    setAddressSubmitting(true);
    try {
      const toCoordinate = (value: unknown) => {
        const coordinate = Number(value ?? 0);
        return Number.isFinite(coordinate) ? coordinate : 0;
      };
      const publicAccessToken = await resolvePublicAccessToken();
      if (!publicAccessToken) {
        throw new Error("Failed to resolve public project access token.");
      }
      await createPublicAddressForProjectUser(token, customerGlobalUserId, publicAccessToken, {
        customerId: customerGlobalUserId,
        organizationId: resolveCustomerOrganizationId(project),
        membershipId: resolveCustomerMembershipId(project),
        globalUserId: customerGlobalUserId,
        userType: "GLOBAL_USER",
        label:
          String(values.label || "").trim() ||
          [project.first_name, project.last_name].filter(Boolean).join(" ").trim() ||
          "Address",
        firstName: String(project.first_name || "").trim(),
        lastName: String(project.last_name || "").trim(),
        company: "",
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        city: values.city,
        state: values.state,
        zip: values.zipCode,
        country: values.country?.trim() || US_COUNTRY_DEFAULTS.country,
        phone: String(project.customer_phone || "").trim(),
        cityCode: String(values.cityCode || "").trim(),
        stateCode: (() => {
          const fromForm = String(values.stateCode || "").trim();
          if (fromForm) return fromForm;
          const stateOnly = String(values.state || "").trim();
          return /^[A-Za-z]{2}$/.test(stateOnly) ? stateOnly.toUpperCase() : "";
        })(),
        countryCode: String(values.countryCode || "").trim() || US_COUNTRY_DEFAULTS.countryCode,
        latitude: toCoordinate(values.latitude),
        longitude: toCoordinate(values.longitude),
      });
      const list = await loadPublicAddresses();
      const newest = list[0];
      const newestId = pickAddressId(newest);
      if (newestId) {
        setSelectedBillingAddressId(newestId);
        if (sameAsBilling) setSelectedShippingAddressId(newestId);
      }
      setShowAddressModal(false);
      showSuccess("Address added successfully.");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to create addresses");
    } finally {
      setAddressSubmitting(false);
    }
  };

  const partyName = project?.agent_name || project?.team_member_name || project?.customer_name || "—";

  const triggerFileDownload = (href: string, fileName: string, fileId?: string) => {
    const id = String(fileId || "").trim();
    const apiDownloadUrl = id ? publicWorkDriveDownloadUrl(id) : "";
    const effectiveHref = apiDownloadUrl || href;
    if (!effectiveHref) {
      showError("Download link unavailable.");
      return;
    }
    const link = document.createElement("a");
    link.href = effectiveHref;
    link.download = fileName || "";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openFilePreview = (f: ClientProjectFile, dateLabel?: string) => {
    const previewUrl = String(f.preview_url || "").trim();
    const href = fileHref(f);
    const name = fileDisplayName(f);
    const imageSrc =
      isLikelyImagePreview(f.file_type, name) && isHttpUrl(previewUrl)
        ? previewUrl
        : isLikelyImagePreview(f.file_type, name) && isHttpUrl(href)
          ? href
          : "";
    if (!href && !previewUrl && !imageSrc) return;
    filePreviewOpenRef.current = true;
    setShowAddressPickerModal(false);
    setFilePreview({
      name,
      previewUrl: imageSrc,
      href: href || previewUrl || imageSrc,
      apiFileId: f.file_id,
      sizeLabel: formatFileSize(f.file_size),
      dateLabel: dateLabel || formatDateTime(f.created_at),
    });
  };

  const closeFilePreview = () => {
    filePreviewOpenRef.current = false;
    setFilePreview(null);
  };

  const unitsBadge = (qty: number) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        background: "#dcfce7",
        color: "#166534",
        borderRadius: 999,
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "#16a34a",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
        }}
      >
        {qty}
      </span>
      {qty === 1 ? "unit" : "units"}
    </span>
  );

  const renderPendingFile = (f: ClientProjectFile, extraChip?: string) => {
    const name = fileDisplayName(f);
    const href = fileHref(f);
    const previewUrl = String(f.preview_url || "").trim();
    const canImg = isLikelyImagePreview(f.file_type, name) && isHttpUrl(previewUrl);
    const typeLabel = fileTypeLabel(f.file_type, name);
    return (
      <div className="pq-attachment pq-attachment--message-file" key={f.id}>
        <div className="pq-attachment-left">
          <div className={`pq-file-type ${typeLabel.toLowerCase()}`}>{typeLabel}</div>
          <div className="pq-attachment-content">
            <div className="pq-attachment-topline">
              <span className="pq-attachment-name">{name}</span>
              {extraChip ? <span className="pq-attachment-proof-chip">{extraChip}</span> : null}
            </div>
          </div>
        </div>
        <div className="pq-attachment-actions">
          {(canImg || Boolean(href)) && (
            <ViewIconButton
              type="button"
              className="pq-attachment-download pq-attachment-view"
              title="View"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openFilePreview(f);
              }}
              size={34}
            />
          )}
          {href ? (
            <DownloadIconButton
              type="button"
              className="pq-attachment-download"
              title="Download"
              onClick={() => triggerFileDownload(href, name, f.file_id)}
              size={34}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const renderApprovedFileRow = (f: ClientProjectFile, index: number) => {
    const name = fileDisplayName(f);
    const href = fileHref(f);
    const letter = String.fromCharCode(65 + index);
    const qty = Number(f.quantity) || 0;
    return (
      <div className="pq-msg-proof-row pq-msg-proof-row--rich" key={f.id}>
        <div className="pq-msg-proof-row-inner">
          <div className="pq-msg-proof-row-top">
            <div className="pq-msg-proof-row-main">
              <i className="ri-file-4-line pq-msg-proof-row-icon" aria-hidden />
              <span className="pq-msg-proof-row-letter">File {letter}</span>
              <span className="pq-msg-proof-row-name" title={name}>{name}</span>
            </div>
            <div className="pq-msg-proof-row-actions">
              {unitsBadge(qty)}
              <ViewIconButton
                type="button"
                className="pq-msg-proof-row-view"
                title="View"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openFilePreview(f);
                }}
                size={36}
              />
              {href ? (
                <DownloadIconButton
                  type="button"
                  className="pq-msg-proof-row-download"
                  title="Download"
                  onClick={() => triggerFileDownload(href, name, f.file_id)}
                  size={36}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPendingSharedRow = (f: ClientProjectFile) => {
    const name = fileDisplayName(f);
    const href = fileHref(f);
    const typeLabel = fileTypeLabel(f.file_type, name);
    return (
      <div className="pq-file-row" key={f.id}>
        <div className={`pq-file-type ${typeLabel.toLowerCase()}`}>{typeLabel}</div>
        <div className="pq-file-meta">
          <div className="pq-file-name">{name}</div>
          <div className="pq-file-sub">
            {f.created_by_name ? <span className="pq-file-sub-name">{f.created_by_name}</span> : null}
            <span className="pq-file-sub-time">{formatDateTime(f.created_at)}</span>
          </div>
        </div>
        <div className="pq-file-actions">
          <ViewIconButton
            type="button"
            className="pq-file-action-btn"
            title="View"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openFilePreview(f);
            }}
            size={28}
          />
          {href ? (
            <DownloadIconButton
              type="button"
              className="pq-file-action-btn"
              title="Download"
              onClick={() => triggerFileDownload(href, name, f.file_id)}
              size={28}
            />
          ) : null}
        </div>
      </div>
    );
  };

  if (loadError && !project) {
    return (
      <div className="project-quote-page pq-page-shell p-5">
        <p className="text-danger mb-3">{loadError}</p>
        <button type="button" className="btn btn-outline-primary" onClick={() => navigate(listPath)}>
          Back to design requests
        </button>
      </div>
    );
  }

  return (
    <div className="project-quote-page pq-page-shell">
      <div className="pq-view-head">
        <div>
          <div className="pq-view-title-row">
            <button className="border pq-back-link px-3 py-2" type="button" onClick={() => navigate(listPath)}>
              <i className="ri-arrow-left-line" />
              <span>Back</span>
            </button>
            <div className="pq-ref-divider" />
            <h1 className="pq-view-title">Design Request Details</h1>
            <span className={statusClass}>{statusDisplay(project?.status)}</span>
          </div>
          <p className="pq-page-subtitle">
            Request ID: <strong className="ms-1">{project?.project_number || projectId || "—"}</strong>
          </p>
        </div>
        <div className="pq-actions-wrap" ref={actionsMenuWrapRef}>
          <button className="pq-btn pq-btn-outline pq-ref-actions" type="button" onClick={() => setShowActions((s) => !s)}>
            <i className="ri-more-2-fill" />
            <span>Actions</span>
            <i className="ri-arrow-down-s-line" />
          </button>
          {showActions ? (
            <div className="pq-actions-menu">
              <button
                type="button"
                onClick={() => {
                  setShowActions(false);
                  if (projectId) navigate(`${listPath}/edit/${projectId}`);
                }}
              >
                <i className="ri-edit-line" />
                Edit
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="pq-layout-two-col">
        <div className="pq-main-col">
          <div className="pq-card pq-section-card">
            <div className="pq-card-head pq-ref-card-head-blue">
              <h3 className="pq-ref-head-title">
                <i className="ri-user-line" />
                Customer Information
              </h3>
            </div>
            <div className="pq-card-body">
              <div className="pq-agent-grid-top">
                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon user"><i className="ri-user-line" /></span>
                  <div>
                    <div className="pq-k">Name</div>
                    <div className="pq-v">{partyName}</div>
                  </div>
                </div>
                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon email"><i className="ri-mail-line" /></span>
                  <div>
                    <div className="pq-k">Email</div>
                    <div className="pq-v">{project?.customer_email || "—"}</div>
                  </div>
                </div>
                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon phone"><i className="ri-phone-line" /></span>
                  <div>
                    <div className="pq-k">Phone</div>
                    <div className="pq-v">{project?.customer_phone || "—"}</div>
                  </div>
                </div>
                <div className="pq-agent-item">
                  <span className="pq-agent-item-icon brokerage"><i className="ri-briefcase-4-line" /></span>
                  <div>
                    <div className="pq-k">Brokerage</div>
                    <div className="pq-v">{project?.brokerage_firm_name || "—"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pq-card pq-section-card">
            <div className="pq-card-head pq-ref-card-head-indigo">
              <h3 className="pq-ref-head-title">
                <i className="ri-file-list-3-line" />
                Request Details
              </h3>
            </div>
            <div className="pq-card-body">
              <div className="pq-k">Design Title</div>
              <div className="pq-v pq-block-value">{project?.title || "—"}</div>
              <div className="pq-split-line" />
              <div className="pq-k">Notes</div>
              <div className="pq-v pq-block-value">{project?.notes || "—"}</div>
              <div className="pq-split-line" />
              <div className="pq-meta-row">
                <div>
                  <div className="pq-k">Vendor</div>
                  <div className="pq-v">{project?.designer_name || project?.assigned_to_name || "—"}</div>
                </div>
                <div>
                  <div className="pq-k">Created Date</div>
                  <div className="pq-v">{formatDateTime(project?.created_at)}</div>
                </div>
                <div>
                  <div className="pq-k">Last Updated</div>
                  <div className="pq-v">{formatDateTime(project?.updated_at)}</div>
                </div>
              </div>
              {project?.linked_order_number ? (
                <>
                  <div className="pq-split-line" />
                  <div className="pq-k">Linked Order</div>
                  <div className="pq-v">
                    {linkedOrderPath ? (
                      <a href={linkedOrderPath}>{project.linked_order_number}</a>
                    ) : (
                      project.linked_order_number
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <div className="pq-card pq-section-card">
            <div className="pq-conversation-card-body">
              <div className="pq-tabs-with-right">
                <button
                  type="button"
                  className={`pq-tab ${tab === "conversation" ? "active" : ""}`}
                  onClick={() => setTab("conversation")}
                >
                  <i className="ri-chat-4-line" />
                  Conversation
                </button>
                <button
                  type="button"
                  className={`pq-tab ${tab === "files" ? "active" : ""}`}
                  onClick={() => setTab("files")}
                >
                  <i className="ri-file-3-line" />
                  Shared Files <span className="pq-tab-badge">{sharedFiles.length}</span>
                </button>
              </div>

              {tab === "conversation" ? (
                <>
                  <div className="pq-convo-list">
                    {messages.length === 0 ? (
                      <p className="text-muted px-2 py-4 mb-0">No messages yet.</p>
                    ) : (
                      messages.map((message) => {
                        const mine = !isVendorSender(message.sender_type);
                        const proofFiles = message.files.filter((f) => f.is_proof);
                        const otherFiles = message.files.filter((f) => !f.is_proof);
                        const proofVersionFromMessage = Number(
                          message.proof_version || proofFiles[0]?.version || 0
                        );
                        const proofApproved =
                          Number.isFinite(proofVersionFromMessage) &&
                          approvedVersionNumbers.has(proofVersionFromMessage);
                        const showApprovedProofCardForMessage =
                          proofApproved &&
                          firstApprovedMessageIdByVersion.get(proofVersionFromMessage) === message.id;
                        const proofMeta = approvedProofMetaByVersion.get(proofVersionFromMessage);
                        const groupedProofFiles =
                          proofVersionGroups.find((g) => g.version === proofVersionFromMessage)?.files ||
                          proofFiles;
                        const pendingProofFiles = proofApproved ? [] : proofFiles;
                        return (
                          <div
                            className={`pq-convo-item${
                              replyToMessageId && replyToMessageId === message.id
                                ? " pq-convo-item--reply-target"
                                : ""
                            }`}
                            key={message.id}
                          >
                            <div className="pq-convo-top">
                              <div className="pq-convo-user">
                                <span className={`pq-convo-avatar ${mine ? "agent" : "designer"}`}>
                                  <i className="ri-user-line" />
                                </span>
                                <div className="pq-convo-user-meta">
                                  <strong>{message.sender_name || (mine ? "You" : "Vendor")}</strong>
                                  <span className="pq-convo-role">
                                    {formatConversationRole(message.sender_type, project?.license_type)}
                                  </span>
                                </div>
                              </div>
                              <div className="pq-convo-top-actions">
                                <span className="pq-convo-time">{formatDateTime(message.created_at)}</span>
                                <button
                                  type="button"
                                  className="pq-reply-btn"
                                  aria-label={`Reply to ${message.sender_name || "message"}`}
                                  title="Reply to this message"
                                  onClick={() => {
                                    setReplyToMessageId(message.id);
                                    requestAnimationFrame(() => {
                                      messageInputRef.current?.focus();
                                    });
                                  }}
                                >
                                  <i className="ri-reply-line" aria-hidden />
                                  <span className="pq-reply-btn-text">Reply</span>
                                </button>
                              </div>
                            </div>
                            <div className="pq-convo-bubble-wrap">
                              {message.reply_to_message_id && messagesById.get(message.reply_to_message_id) ? (
                                <div className="pq-reply-inline">
                                  <strong>
                                    {messagesById.get(message.reply_to_message_id)?.sender_name || "Message"}
                                  </strong>
                                  <span>
                                    {messagesById.get(message.reply_to_message_id)?.message || "(attachment)"}
                                  </span>
                                </div>
                              ) : null}
                              <div
                                className={`pq-convo-bubble-body ${
                                  mine ? "pq-convo-bubble-body--agent" : "pq-convo-bubble-body--admin"
                                }`}
                              >
                                {message.message ? <p className="pq-convo-text">{message.message}</p> : null}
                                {showApprovedProofCardForMessage ? (
                                  <ApprovedProofCard
                                    version={proofVersionFromMessage}
                                    fileCount={groupedProofFiles.length}
                                    attribution={
                                      project?.design_approved_by?.name ? (
                                        <span className="pq-msg-proof-by">
                                          by {project.design_approved_by.name}
                                        </span>
                                      ) : undefined
                                    }
                                    files={groupedProofFiles.map((f) => ({
                                      id: f.id,
                                      name: fileDisplayName(f),
                                      quantity: f.quantity,
                                    }))}
                                    fileQuantities={proofMeta?.fileQuantities || []}
                                    showQtyPerFileDetails={false}
                                    billingAddress={proofMeta?.billingAddress ?? project?.billing_address}
                                    shippingAddress={proofMeta?.shippingAddress ?? project?.shipping_address}
                                    poNumber={
                                      proofMeta?.poNumber ??
                                      (project as any)?.po_number ??
                                      (project as any)?.poNumber ??
                                      ""
                                    }
                                    quantity={Number(proofMeta?.quantity ?? project?.quantity ?? 1)}
                                    shippingMethod={proofMeta?.shippingMethod ?? project?.shipping_method}
                                    filesContent={groupedProofFiles.map((f, i) => renderApprovedFileRow(f, i))}
                                  />
                                ) : null}
                                {pendingProofFiles.length || otherFiles.length ? (
                                  <div
                                    className={
                                      pendingProofFiles.length
                                        ? "pq-attachments pq-attachments--proof-pending"
                                        : "pq-attachments"
                                    }
                                  >
                                    {pendingProofFiles.map((f) =>
                                      renderPendingFile(
                                        f,
                                        Number.isFinite(Number(f.version)) && Number(f.version) > 0
                                          ? `Proof V${f.version}`
                                          : "Proof"
                                      )
                                    )}
                                    {otherFiles.map((f) => renderPendingFile(f))}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="pq-convo-input-wrap">
                    <div className="pq-split-line mt-3" />
                    {replyingToMessage ? (
                      <div className="pq-composer-section pq-composer-section--reply">
                        <div className="pq-replying-bar">
                          <div className="pq-replying-meta">
                            <strong>Replying to {replyingToMessage.sender_name || "message"}</strong>
                            <span>{replyingToMessage.message || "(attachment)"}</span>
                          </div>
                          <button
                            type="button"
                            className="pq-replying-cancel"
                            onClick={() => setReplyToMessageId("")}
                            aria-label="Cancel reply"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      </div>
                    ) : null}
                    <div className="pq-composer-border-wrap">
                      <div className="pq-composer-section pq-composer-section--text">
                        <textarea
                          ref={messageInputRef}
                          className="pq-msg-input"
                          placeholder="Type your message to the vendor..."
                          value={messageDraft}
                          onChange={(e) => setMessageDraft(e.target.value)}
                        />
                      </div>
                      {attachedFiles.length ? (
                        <div className="pq-composer-section pq-composer-section--attachments">
                          <div className="pq-attached-list">
                            {attachedFiles.map((file, idx) => (
                              <div key={`${file.name}-${idx}`} className="pq-attached-pill">
                                <i className="ri-file-2-line" />
                                <span className="pq-attached-pill-name">{file.name}</span>
                                <button
                                  type="button"
                                  className="pq-attached-pill-remove"
                                  onClick={() => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx))}
                                >
                                  <i className="ri-close-line" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <div className="pq-msg-actions--composer">
                        <label className="pq-attach-label">
                          <i className="ri-attachment-2" />
                          Attach
                          <input
                            type="file"
                            multiple
                            hidden
                            onChange={(e) => {
                              if (e.target.files?.length) {
                                setAttachedFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
                              }
                              e.currentTarget.value = "";
                            }}
                          />
                        </label>
                        <button
                          className="pq-send-btn"
                          type="button"
                          disabled={sending || (!messageDraft.trim() && !attachedFiles.length)}
                          onClick={() => void handleSend()}
                        >
                          <i className="ri-send-plane-fill" />
                          Send
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="pq-shared-files">
                  {proofVersionGroups.map(({ version, files }) => {
                    const groupApproved = approvedVersionNumbers.has(version);
                    const proofMeta = approvedProofMetaByVersion.get(version);
                    return groupApproved ? (
                      <div className="pq-proof-group pq-proof-group--proof-chat" key={`proof-v-${version}`}>
                        <div className="pq-msg-proof-approved pq-msg-proof-approved--embedded">
                          <ApprovedProofCard
                            version={version}
                            fileCount={files.length}
                            attribution={
                              project?.design_approved_by?.name ? (
                                <span className="pq-msg-proof-by">by {project.design_approved_by.name}</span>
                              ) : undefined
                            }
                            files={files.map((f) => ({
                              id: f.id,
                              name: fileDisplayName(f),
                              quantity: f.quantity,
                            }))}
                            fileQuantities={proofMeta?.fileQuantities || []}
                            showQtyPerFileDetails={false}
                            billingAddress={proofMeta?.billingAddress ?? project?.billing_address}
                            shippingAddress={proofMeta?.shippingAddress ?? project?.shipping_address}
                            poNumber={
                              proofMeta?.poNumber ??
                              (project as any)?.po_number ??
                              (project as any)?.poNumber ??
                              ""
                            }
                            quantity={Number(proofMeta?.quantity ?? project?.quantity ?? 1)}
                            shippingMethod={proofMeta?.shippingMethod ?? project?.shipping_method}
                            filesContent={files.map((f, i) => renderApprovedFileRow(f, i))}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="pq-proof-group w-60" key={`proof-v-${version}`}>
                        <div className="pq-proof-group-head pq-proof-group-head--proof-pending">
                          <span className="pq-proof-group-head-proof-label">
                            <i className="ri-file-list-3-line" aria-hidden />
                            <span className="pq-proof-chip">Proof V{version}</span>
                            <span className="pq-proof-group-head-count">
                              {files.length} file{files.length !== 1 ? "s" : ""}
                            </span>
                          </span>
                        </div>
                        {files.map((f) => renderPendingSharedRow(f))}
                      </div>
                    );
                  })}

                  {otherSharedFiles.length > 0 ? (
                    <div className="pq-proof-group">
                      <div className="pq-proof-group-head plain">OTHER FILES</div>
                      {otherSharedFiles.map((f) => renderPendingSharedRow(f))}
                    </div>
                  ) : null}

                  {proofVersionGroups.length === 0 && otherSharedFiles.length === 0 ? (
                    <p className="text-muted py-4 mb-0">No shared files yet.</p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pq-side-col">
          <div className="pq-side-section">
            <h5 className="pq-side-title pq-side-title--quick-actions">
              <i className="ri-flashlight-line" />
              Quick Actions
            </h5>
            <div className="pq-side-box pq-proof-version-card">
              <div className="pq-proof-version-card-head">
                <i className="ri-file-3-line" aria-hidden />
                <span>SELECT PROOF VERSION</span>
              </div>
              {proofVersionGroups.length === 0 ? (
                <p className="pq-proof-version-empty text-muted small mb-0 p-3">No proof versions yet.</p>
              ) : (
                <ul className="pq-proof-version-list" role="radiogroup" aria-label="Proof version">
                  {proofVersionGroups.map(({ version, files }) => {
                    const rowKey = `v-${version}`;
                    const isSelected = selectedProofVersion === version;
                    const isApproved = approvedVersionNumbers.has(version);
                    return (
                      <li key={rowKey} className="pq-proof-version-item">
                        <label
                          className={`pq-proof-version-label ${isSelected ? "is-selected" : ""} ${
                            isApproved ? "is-approved" : ""
                          }`}
                        >
                          <span className="pq-proof-version-radio-cell">
                            {!isApproved ? (
                              <>
                                <input
                                  type="radio"
                                  name="proof-version"
                                  className="pq-proof-version-radio-input"
                                  checked={isSelected}
                                  onChange={() => setSelectedProofVersion(version)}
                                />
                                <span className="pq-proof-version-radio-faux" aria-hidden />
                              </>
                            ) : null}
                          </span>
                          <span className="pq-proof-version-body">
                            <span className="pq-proof-version-title-row">
                              <span className="pq-proof-version-title">
                                Proof V{version}
                                {isApproved ? (
                                  <span style={{ color: "rgb(22, 163, 74)", fontWeight: 600 }}>Approved</span>
                                ) : null}
                              </span>
                              <span className="pq-proof-version-file-count">
                                {files.length} file{files.length !== 1 ? "s" : ""}
                              </span>
                            </span>
                            <span className="pq-proof-version-chips">
                              {files.map((pf) => (
                                <span
                                  key={pf.id}
                                  className="pq-proof-version-chip"
                                  title={fileDisplayName(pf)}
                                >
                                  <i className="ri-file-text-line" aria-hidden />
                                  <span className="pq-proof-version-chip-name">{fileDisplayName(pf)}</span>
                                </span>
                              ))}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
              <button
                className={`pq-proof-version-submit pq-btn pq-btn-outline pq-full-btn ${
                  !selectedProofVersion || approving || selectedProofApproved ? "pq-btn-disabled" : ""
                }`}
                type="button"
                disabled={!selectedProofVersion || approving || selectedProofApproved}
                onClick={() => void handleApprove()}
              >
                <i className="ri-checkbox-circle-line" aria-hidden />
                {approving ? "Approving..." : "Mark as Approved"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {filePreview ? (
        <div
          className="pq-file-preview-backdrop"
          role="presentation"
          style={{ zIndex: 12000 }}
          onClick={closeFilePreview}
        >
          <div
            className="pq-file-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pq-file-preview-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pq-file-preview-head">
              <div className="pq-file-preview-title-wrap">
                <i className="ri-file-2-line" aria-hidden />
                <h2 id="pq-file-preview-title" className="pq-file-preview-title">
                  {filePreview.name}
                </h2>
              </div>
              <button
                type="button"
                className="pq-file-preview-close"
                aria-label="Close preview"
                onClick={closeFilePreview}
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="pq-file-preview-body">
              {filePreview.previewUrl ? (
                <img className="pq-file-preview-img" src={filePreview.previewUrl} alt="" />
              ) : (
                <div className="pq-file-preview-fallback">
                  <i className="ri-file-line" aria-hidden />
                  <p>Preview not available for this file type.</p>
                </div>
              )}
            </div>
            <div className="pq-file-preview-foot">
              <span>
                {filePreview.sizeLabel} · {filePreview.dateLabel}
              </span>
              {filePreview.href ? (
                <button
                  type="button"
                  className="pq-btn pq-btn-outline"
                  onClick={() =>
                    triggerFileDownload(filePreview.href, filePreview.name, filePreview.apiFileId)
                  }
                >
                  <i className="ri-download-2-line" />
                  Download
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <BillingShippingApprovalModal
        open={showAddressPickerModal && !filePreview}
        loading={addressSubmitting || approving}
        sameAsBilling={sameAsBilling}
        setSameAsBilling={setSameAsBilling}
        selectedBillingAddressId={selectedBillingAddressId}
        setSelectedBillingAddressId={setSelectedBillingAddressId}
        selectedShippingAddressId={selectedShippingAddressId}
        setSelectedShippingAddressId={setSelectedShippingAddressId}
        addressOptions={publicAddresses.map((addr) => ({
          value: pickAddressId(addr),
          label: formatAddressSingleLine(addr) || addr.label || "Address",
        }))}
        shippingMethods={SHIPPING_METHODS}
        selectedShippingMethodId={selectedShippingMethodId}
        setSelectedShippingMethodId={setSelectedShippingMethodId}
        proofFiles={selectedProofFilesForApproval}
        fileQuantities={selectedFileQuantities}
        setFileQuantity={(fileId, qty) => {
          setSelectedFileQuantities((prev) => ({ ...prev, [fileId]: qty }));
        }}
        poNumber={approvalPoNumber}
        setPoNumber={setApprovalPoNumber}
        confirmLabel="Confirm & Approve Design"
        onAddNewAddress={() => setShowAddressModal(true)}
        onClose={() => {
          if (addressSubmitting || approving) return;
          setShowAddressPickerModal(false);
        }}
        onConfirm={() => void handleConfirmAddressAndApprove()}
      />
      <AddAddressModal
        open={showAddressModal}
        loading={addressSubmitting}
        onClose={() => {
          if (addressSubmitting) return;
          setShowAddressModal(false);
        }}
        onSubmit={handleSubmitApprovalAddress}
      />
    </div>
  );
};

export default DesignRequestView;
